import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { formatDateOnly } from "@/lib/date-only";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  ClipboardCheck,
  Plus,
  ArrowRight,
  CheckCircle,
  XCircle,
  Clock,
} from "lucide-react";

const inspectionsSearchSchema = z.object({
  status: z.string().optional(),
});

export const Route = createFileRoute("/_authenticated/inspections")({
  validateSearch: inspectionsSearchSchema,
  component: InspectionsPage,
});

interface InspectionSummary {
  id: string;
  date: string;
  status: string;
  overallResult: string | null;
  templateName: string | null;
  inspectorName: string | null;
  createdAt: string;
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
] as const;

function InspectionsPage() {
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const statusFilter = search.status ?? "all";
  const setStatusFilter = (value: string) => {
    navigate({
      search: (prev) => ({
        ...prev,
        status: value === "all" ? undefined : value,
      }),
      replace: true,
    });
  };

  const { data, isLoading } = useQuery({
    queryKey: ["inspections", statusFilter],
    queryFn: () =>
      apiGet<{ data: InspectionSummary[]; total: number }>(
        `/api/inspections?status=${statusFilter}&limit=50`
      ),
  });

  const inspections = data?.data || [];

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <div className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-bold text-text-primary">Inspections</h1>
        <Link to="/inspections/new">
          <Button size="sm" className="bg-paris-red hover:bg-paris-red-dark gap-1">
            <Plus className="h-4 w-4" />
            New
          </Button>
        </Link>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.value}
            type="button"
            onClick={() => setStatusFilter(filter.value)}
            className={cn(
              "inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors shrink-0",
              statusFilter === filter.value
                ? "bg-paris-red text-white"
                : "bg-card text-text-secondary border border-border hover:bg-muted/50 hover:text-text-primary"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && inspections.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ClipboardCheck className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">
              No inspections
            </p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Start an inspection from a template.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && inspections.length > 0 && (
        <div className="space-y-2">
          {inspections.map((inspection) => (
            <Link
              key={inspection.id}
              to="/inspections/$inspectionId"
              params={{ inspectionId: inspection.id }}
            >
              <Card className="hover:bg-muted/30 transition-colors cursor-pointer">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        {inspection.templateName || "Inspection"}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        {inspection.status === "completed" ? (
                          inspection.overallResult === "pass" ? (
                            <Badge className="bg-green-100 text-green-800 text-xs px-1.5 py-0 gap-0.5">
                              <CheckCircle className="h-3 w-3" />
                              Pass
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 text-xs px-1.5 py-0 gap-0.5">
                              <XCircle className="h-3 w-3" />
                              Fail
                            </Badge>
                          )
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-800 text-xs px-1.5 py-0 gap-0.5">
                            <Clock className="h-3 w-3" />
                            In Progress
                          </Badge>
                        )}
                        <span className="text-xs text-muted-foreground">
                          {formatDateOnly(inspection.date)}
                        </span>
                      </div>
                      {inspection.inspectorName && (
                        <p className="text-xs text-muted-foreground mt-1">
                          By: {inspection.inspectorName}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
